import { getCompanyId } from "../Functions/AuthFunction";
import { ref, set } from "firebase/database";
import { db } from "./firebase";
import { v4 as uuidv4 } from "uuid";

const COMPANIES_PATH = "contacthq/contact-support";

export const submitSupportInquiry = async (formData) => {
  const inquiryId = uuidv4(); // Generate unique ticket ID
  const timestamp = new Date().toISOString();
  const enrichedData = {
    // Primary Data from Form
    id: inquiryId,
    name: formData.name,
    email: formData.email,
    subject: formData.subject,
    message: formData.message,
    // Automation & Management Fields
    status: "pending", // Default status for the Centralized Inbox
    priority: "normal", // Can be scaled based on subject
    formType: formData.subject || "General Inquiry",
    // Tracking & Metadata
    createdAt: timestamp,
    updatedAt: timestamp,
    userAgent: navigator.userAgent, // Helpful for debugging technical issues
    platformSource: "ContactHQ_Web_Landing",
    // Optional: If you want to track which company the user belongs to (if logged in)
    submittedByCompanyId: getCompanyId() || "guest_user",
  };

  try {
    const supportRef = ref(db, `${COMPANIES_PATH}/${inquiryId}`);
    await set(supportRef, enrichedData);

    return {
      success: true,
      message: "Ticket created in Centralized Inbox",
      id: inquiryId,
    };
  } catch (error) {
    console.error("Submission Error:", error);
    return {
      success: false,
      message: "Failed to connect to ContactHQ servers. Please try again.",
    };
  }
};

export const submitBugReport = async (bugData) => {
  const reportId = uuidv4();
  const timestamp = new Date().toISOString();
  const enrichedBugData = {
    // Primary Data from Form
    id: reportId,
    title: bugData.title,
    severity: bugData.severity, // Low, Medium, High, Critical
    environment: bugData.environment, // e.g., Chrome 120, Windows 11
    stepsToReproduce: bugData.steps,
    expectedBehavior: bugData.expected,
    actualResult: bugData.actual,

    // Automation & Management Fields
    status: "unresolved", // Default status for engineering review
    priority: bugData.severity === "Critical" ? "P0" : "P1",
    category: "Technical_Bug",

    // Tracking & Developer Metadata
    createdAt: timestamp,
    updatedAt: timestamp,
    companyId: getCompanyId() || "public_reporter", // Tracks which user found the bug
    deviceInfo: {
      userAgent: navigator.userAgent,
      screenResolution: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
    },
  };

  try {
    // Reference to the bug report path
    const bugRef = ref(db, `contacthq/bug-reports/${reportId}`);

    // Saving to Firebase
    await set(bugRef, enrichedBugData);

    return {
      success: true,
      message: "Technical report logged successfully",
      id: reportId,
    };
  } catch (error) {
    console.error("Bug Reporting Error:", error);
    return {
      success: false,
      message: "Database connection failed. Please try again later.",
    };
  }
};

export const submitProjectInquiry = async (formData) => {
  const inquiryId = uuidv4();
  const timestamp = new Date().toISOString();

  const formattedService = formData.service
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const enrichedInquiryData = {
    // Primary User Data
    id: inquiryId,
    sender: formData.fullName, // Mapped to 'sender' for QueriesTable compatibility
    email: formData.email,
    phone: formData.phone,
    companyName: formData.companyName || "N/A",

    // Specific Form Answers
    serviceRequested: formData.service,
    budget: formData.budget,
    message: formData.projectDetails,

    // Automation & UI Management Fields
    formCategory: "Project Lead", // Shows up as a badge in the Queries Table
    formTitle: `New Lead: ${formattedService}`, // Shows up as the Subject
    status: "pending", // 'pending' triggers the blue "Unread" UI highlight
    priority: "High", // Sales inquiries are High priority

    // Tracking & Metadata
    createdAt: timestamp,
    submittedAt: timestamp, // Used by formatDistanceToNow in QueriesTable
    deviceInfo: {
      userAgent: navigator.userAgent,
      screenResolution: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
    },
  };

  try {
    // Saving directly to the contact-support node so it routes to your Queries Inbox
    const inquiryRef = ref(db, `contacthq/service-leads/${inquiryId}`);
    await set(inquiryRef, enrichedInquiryData);

    return {
      success: true,
      message: "Project inquiry submitted successfully",
      id: inquiryId,
    };
  } catch (error) {
    console.error("Project Inquiry Submission Error:", error);
    return {
      success: false,
      message: "Database connection failed. Please try again later.",
    };
  }
};
